import React, { useState } from 'react';
import { UserPlus, Users, Upload, Loader2, ListPlus } from 'lucide-react';
import { toast } from 'sonner';
import { GROUPS } from './NewsletterComposer';
import { addSubscriber, addSubscribersBulk, addSubscribersFromMembers, importSubscribers } from '../../lib/api';

const inputCls = 'w-full px-3 py-2.5 bg-white border border-neutral-200 rounded-sm font-sans-ah text-[14px] focus:outline-none focus:border-[#4a9cd4]';
const outlineBtn = 'font-sans-ah text-[12px] tracking-wide-ah px-4 py-2.5 rounded-sm border border-neutral-300 text-[#555] hover:bg-white flex items-center gap-2 disabled:opacity-50';
function GroupPicker({ value, onChange, testid }) {
  const toggle = (k) => onChange(value.includes(k) ? value.filter((x) => x !== k) : [...value, k]);
  return (
    <div className="flex flex-wrap items-center gap-1.5" data-testid={testid}>
      <span className="font-sans-ah text-[11px] tracking-wide-ah text-[#777] uppercase mr-1">Groups</span>
      {GROUPS.map(([k, label]) => (
        <button key={k} type="button" onClick={() => toggle(k)} data-testid={`${testid}-${k}`}
          className={`font-sans-ah text-[11px] px-2.5 py-1 rounded-full border transition-colors ${value.includes(k) ? 'bg-[#4a9cd4] border-[#4a9cd4] text-white' : 'border-neutral-300 text-[#888] hover:border-[#4a9cd4]'}`}>{label}</button>
      ))}
    </div>
  );
}

const errMsg = (e) => (typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : 'Something went wrong');

const report = (r) => {
  const parts = [`${r.added} added`];
  if (r.skipped) parts.push(`${r.skipped} already subscribed`);
  if (r.invalid_count) parts.push(`${r.invalid_count} invalid`);
  toast.success(parts.join(' • '));
  if (r.invalid_count) toast.message(`Skipped invalid: ${r.invalid.slice(0, 5).join(', ')}${r.invalid_count > 5 ? '…' : ''}`);
};

function SingleAdd({ onDone }) {
  const [f, setF] = useState({ email: '', name: '', send_welcome: false, groups: ['public'] });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await addSubscriber(f);
      toast.success(`${f.email} added${f.send_welcome ? ' — welcome email sent' : ''}`);
      setF({ email: '', name: '', send_welcome: false, groups: ['public'] }); onDone();
    } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };
  return (
    <form onSubmit={submit} className="bg-white border border-neutral-100 rounded-sm p-5" data-testid="add-subscriber-form">
      <h3 className="font-serif-ah text-[19px] text-[#2f2f2f] mb-3 flex items-center gap-2"><UserPlus size={18} className="text-[#4a9cd4]" /> Add one subscriber</h3>
      <div className="grid sm:grid-cols-2 gap-3">
        <div><label htmlFor="sub-email" className="font-sans-ah text-[11px] tracking-wide-ah text-[#777] uppercase">Email *</label>
          <input id="sub-email" type="email" required data-testid="add-subscriber-email-input" className={inputCls} value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></div>
        <div><label htmlFor="sub-name" className="font-sans-ah text-[11px] tracking-wide-ah text-[#777] uppercase">Name (optional)</label>
          <input id="sub-name" data-testid="add-subscriber-name-input" className={inputCls} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></div>
      </div>
      <div className="mt-3"><GroupPicker value={f.groups} onChange={(groups) => setF({ ...f, groups })} testid="add-subscriber-groups" /></div>
      <div className="flex flex-wrap items-center justify-between gap-3 mt-4">
        <label className="flex items-center gap-2 font-sans-ah text-[13px] text-[#555]">
          <input type="checkbox" data-testid="add-subscriber-welcome-checkbox" checked={f.send_welcome} onChange={(e) => setF({ ...f, send_welcome: e.target.checked })} /> Send welcome email
        </label>
        <button type="submit" disabled={busy} data-testid="add-subscriber-submit-btn" className="btn-ah-solid font-sans-ah text-[12px] px-5 py-2.5 rounded-sm flex items-center gap-2 disabled:opacity-60">
          {busy && <Loader2 size={14} className="animate-spin" />} ADD SUBSCRIBER
        </button>
      </div>
    </form>
  );
}

function BulkAdd({ onDone }) {
  const [text, setText] = useState('');
  const [groups, setGroups] = useState(['public']);
  const [busy, setBusy] = useState('');
  const run = async (kind, fn) => {
    setBusy(kind);
    try { report(await fn()); onDone(); return true; } catch (err) { toast.error(errMsg(err)); return false; } finally { setBusy(''); }
  };
  const paste = async () => { if (text.trim() && await run('paste', () => addSubscribersBulk(text, groups))) setText(''); };
  const upload = (e) => { const file = e.target.files?.[0]; e.target.value = ''; if (file) run('file', () => importSubscribers(file, groups)); };
  const members = () => window.confirm('Add every member profile email to the subscriber list? Existing subscribers are skipped and no emails are sent.') && run('members', addSubscribersFromMembers);
  return (
    <div className="bg-white border border-neutral-100 rounded-sm p-5" data-testid="bulk-subscribers-panel">
      <h3 className="font-serif-ah text-[19px] text-[#2f2f2f] mb-3 flex items-center gap-2"><ListPlus size={18} className="text-[#4a9cd4]" /> Add in bulk</h3>
      <label htmlFor="bulk-emails" className="font-sans-ah text-[11px] tracking-wide-ah text-[#777] uppercase">Paste emails (separated by commas, spaces or new lines)</label>
      <textarea id="bulk-emails" rows={3} data-testid="bulk-subscribers-textarea" className={inputCls} value={text} onChange={(e) => setText(e.target.value)} placeholder="jane@example.com, owner@business.ca" />
      <div className="mt-3"><GroupPicker value={groups} onChange={setGroups} testid="bulk-subscribers-groups" /></div>
      <div className="flex flex-wrap gap-2 mt-3">
        <button onClick={paste} disabled={!!busy || !text.trim()} data-testid="bulk-subscribers-add-btn" className="btn-ah-solid font-sans-ah text-[12px] px-4 py-2.5 rounded-sm flex items-center gap-2 disabled:opacity-50">
          {busy === 'paste' && <Loader2 size={14} className="animate-spin" />} ADD PASTED EMAILS
        </button>
        <label data-testid="bulk-subscribers-file-btn" className={`${outlineBtn} cursor-pointer`}>
          {busy === 'file' ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} UPLOAD CSV / EXCEL
          <input type="file" accept=".csv,.xlsx,.xls,.txt" className="hidden" data-testid="bulk-subscribers-file-input" onChange={upload} disabled={!!busy} />
        </label>
        <button onClick={members} disabled={!!busy} data-testid="subscribers-from-members-btn" className={outlineBtn}>
          {busy === 'members' ? <Loader2 size={14} className="animate-spin" /> : <Users size={14} />} ADD ALL MEMBER EMAILS
        </button>
      </div>
      <p className="font-sans-ah text-[12px] text-[#999] mt-2">Files: any column with "email" in its header is used. Pasted/uploaded emails join the selected groups. Member emails always join "Members". Duplicates are skipped and no emails are sent.</p>
    </div>
  );
}

export function AddSubscribers({ onDone }) {
  return (
    <div className="grid lg:grid-cols-2 gap-4 mb-8">
      <SingleAdd onDone={onDone} />
      <BulkAdd onDone={onDone} />
    </div>
  );
}
